'use client';

import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Smartphone, MessageSquare, Mic, Wallet, Bell, Settings, Menu, X, LogOut, User, BarChart3, Plus, Shield, Key } from 'lucide-react';
import { useAuth } from '@/providers/auth-provider';
import { useRealtime } from '@/providers/realtime-provider';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/numbers', label: 'Numbers', icon: Smartphone },
  { href: '/sms', label: 'SMS Inbox', icon: MessageSquare },
  { href: '/voice', label: 'Voice', icon: Mic },
  { href: '/wallet', label: 'Wallet', icon: Wallet },
  { href: '/notifications', label: 'Notifications', icon: Bell },
  { href: '/developer/api-keys', label: 'API Keys', icon: Key },
  { href: '/developer/webhooks', label: 'Webhooks', icon: Shield },
  { href: '/sessions', label: 'Sessions', icon: User },
  { href: '/settings', label: 'Settings', icon: Settings },
];

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const { isConnected, connect } = useRealtime();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [lastActivity, setLastActivity] = React.useState(Date.now());

  React.useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());
    ['mousedown', 'mousemove', 'keydown', 'touchstart'].forEach(event =>
      window.addEventListener(event, updateActivity)
    );
    return () => ['mousedown', 'mousemove', 'keydown', 'touchstart'].forEach(event =>
      window.removeEventListener(event, updateActivity)
    );
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (!isCollapsed && Date.now() - lastActivity > 5000) {
        setIsCollapsed(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isCollapsed, lastActivity]);

  React.useEffect(() => {
    connect();
  }, [connect]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-950">
        <AnimatePresence mode="wait">
          {!isCollapsed && (
            <motion.nav
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 glass-strong rounded-full px-6 py-3 shadow-2xl border border-slate-700/50"
              onMouseEnter={() => setLastActivity(Date.now())}
              onMouseLeave={() => setIsCollapsed(true)}
            >
              <div className="flex items-center gap-1">
                {NAV_ITEMS.map((item) => (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>
                      <motion.a
                        href={item.href}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        className={cn(
                          'relative flex items-center justify-center w-12 h-12 rounded-xl text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-all duration-200',
                          location.pathname === item.href && 'text-cyan-300 bg-cyan-500/10'
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        <TooltipContent side="top" className="glass-strong text-xs">
                          {item.label}
                        </TooltipContent>
                      </motion.a>
                    </TooltipTrigger>
                  </Tooltip>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {isCollapsed && (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
              onMouseEnter={() => setIsCollapsed(false)}
              onClick={() => setIsCollapsed(false)}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.div
                    whileHover={{ scale: 1.2 }}
                    className="w-3 h-3 rounded-full bg-gradient-to-r from-cyan-400 to-purple-500 animate-pulse cursor-pointer"
                    style={{ boxShadow: '0 0 20px hsl(var(--neon-cyan)), 0 0 40px hsl(var(--neon-purple))' }}
                  />
                  <TooltipContent side="top" className="glass-strong text-xs">
                    Click to expand navigation
                  </TooltipContent>
                </TooltipTrigger>
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          className="fixed bottom-6 right-6 z-50 md:hidden glass-strong p-3 rounded-xl shadow-xl"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
        </button>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 md:hidden bg-black/50"
              onClick={() => setMobileMenuOpen(false)}
            >
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed inset-y-0 right-0 w-80 glass-strong border-l border-slate-700/50"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-white">Navigation</h2>
                    <button onClick={() => setMobileMenuOpen(false)} className="text-slate-400 hover:text-white">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <nav className="space-y-2">
                    {NAV_ITEMS.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-all',
                          location.pathname === item.href && 'text-cyan-300 bg-cyan-500/10'
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </nav>
                  <Separator className="my-4" />
                  <div className="flex items-center gap-3 px-4 py-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.email ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}` : undefined} alt={user?.email || 'User'} />
                      <AvatarFallback>{user?.email?.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                      <Badge variant="secondary" className="text-xs">{user?.tier}</Badge>
                    </div>
                  </div>
                  <Button variant="destructive" className="w-full" onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <main className="min-h-screen pb-32 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-full text-xs">
                      <motion.span
                        animate={{
                          boxShadow: isConnected ? '0 0 10px hsl(var(--neon-green))' : '0 0 10px hsl(var(--neon-amber))'
                        }}
                        className={cn(
                          'w-2 h-2 rounded-full',
                          isConnected ? 'bg-green-400' : 'bg-amber-400'
                        )}
                      />
                      <span className="text-slate-300">{isConnected ? 'Live' : 'Connecting...'}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="glass-strong">
                    Real-time connection status
                  </TooltipContent>
                </Tooltip>

                {user?.role === 'admin' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/admin" className="flex items-center gap-2 px-3 py-1.5 glass rounded-full text-xs text-amber-400 hover:text-amber-300 transition-colors">
                        <BarChart3 className="h-3 w-3" />
                        <span>Admin Panel</span>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="glass-strong">
                      Go to admin dashboard
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="relative h-10 w-10 rounded-xl">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={user?.email ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}` : undefined} alt={user?.email || 'User'} />
                            <AvatarFallback>{user?.email?.charAt(0).toUpperCase()}</AvatarFallback>
                          </Avatar>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="glass-strong w-56" align="end">
                        <div className="px-4 py-2 border-b border-slate-700/50">
                          <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                          <p className="text-xs text-slate-400">{user?.role} • {user?.tier}</p>
                        </div>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/profile" className="flex items-center gap-2 w-full">
                            <User className="h-4 w-4" />
                            Profile
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/wallet" className="flex items-center gap-2 w-full">
                            <Wallet className="h-4 w-4" />
                            Wallet
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/settings" className="flex items-center gap-2 w-full">
                            <Settings className="h-4 w-4" />
                            Settings
                          </Link>
                        </DropdownMenuItem>
                        {user?.role === 'admin' && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild>
                              <Link to="/admin" className="flex items-center gap-2 w-full text-amber-400">
                                <BarChart3 className="h-4 w-4" />
                                Admin Panel
                              </Link>
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-300">
                          <LogOut className="mr-2 h-4 w-4" />
                          Logout
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="glass-strong">
                    Account menu
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            <Outlet />
          </div>
        </main>

        <div className="fixed bottom-24 right-6 md:bottom-32 z-40">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="icon" className="glass-strong h-12 w-12 rounded-xl shadow-xl hover:shadow-[0_0_30px_rgba(6,182,212,0.3)] transition-all">
                <Plus className="h-6 w-6 text-cyan-300" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="glass-strong">
              Quick actions
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}